import { EmptyState, FlexColumn, FlexRow, Loading, useLoading } from '@gorila-shared-ui/components';
import { StyledDivider } from 'baseui/divider';
import { FlexGrid, FlexGridItem } from 'baseui/flex-grid';
import { StyledCell, StyledHead, StyledHeadCell, StyledRow, StyledTable } from 'baseui/table';
import { LabelLarge } from 'baseui/typography';
import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { TABLE_COLUMNS_NAME, TABLE_MIN_WIDTH } from '../../../constants/fleetOperationals';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import { FleetOperational } from '../../../types/fleet';
import { getFormattedNumber } from '../../../utils/numbers';
import { StyledCard } from '../../shared/styledCard/StyledCard';
import { StyledCardBodyWrapper } from '../../shared/styledCard/StyledCardBodyWrapper';
import { useFleetOperational } from '../hooks/useFleetOperational';

function FleetOperationalList() {
  const { theme, css, classes } = useStyles();
  const [operationals, setOperationals] = useState<FleetOperational[]>();
  const {
    loadFleetOperationalByAsset,
    loadFleetOperationalByGroup,
    reload,
    onUpdated,
    filters: { endDate, searchType, selectedAssetId, selectedGroupId, startDate },
  } = useFleetOperational();
  const { loading, stopLoading, startLoading } = useLoading();
  const { showFailFeedback } = useFeedback();
  const tableColumnsName = TABLE_COLUMNS_NAME;

  useEffect(() => {
    const loadData = async () => {
      startLoading();
      await loadOperationals();
      stopLoading();
      onUpdated();
    };

    loadData();
  }, [reload]);

  useEffect(() => {
    const loadData = async () => {
      startLoading();
      await loadOperationals();
      stopLoading();
    };

    loadData();
  }, [endDate, selectedAssetId, selectedGroupId, startDate]);

  useEffect(() => {
    setOperationals(undefined);
  }, [searchType]);

  const resume = useMemo(() => {
    const refuelTotal = operationals?.reduce((prev, current) => prev + current.refuel, 0) ?? 0;
    const refuelCount = operationals?.reduce((prev, current) => prev + current.refuels, 0) ?? 0;
    const maintenanceTotal = operationals?.reduce((prev, current) => prev + current.maintenanceCosts, 0) ?? 0;
    const maintenanceCount = operationals?.reduce((prev, current) => prev + current.maintenances, 0) ?? 0;
    const repairTotal = operationals?.reduce((prev, current) => prev + current.reparation_costs, 0) ?? 0;
    const repairCount = operationals?.reduce((prev, current) => prev + current.reparations, 0) ?? 0;

    return [
      {
        label: refuelCount === 1 ? 'Carga de combustible' : 'Cargas de combustible',
        total: refuelTotal,
        count: refuelCount,
        color: theme.colors.accent,
      },
      {
        label: maintenanceCount === 1 ? 'Mantenimiento' : 'Mantenimientos',
        total: maintenanceTotal,
        count: maintenanceCount,
        color: theme.colors.negative,
      },
      {
        label: repairCount === 1 ? 'Reparación' : 'Reparaciones',
        total: repairTotal,
        count: repairCount,
        color: theme.colors.positive,
      },
    ];
  }, [operationals]);

  const loadOperationals = async () => {
    if (!selectedAssetId && !selectedGroupId) return;
    const { data, error } = await (searchType === 0 ? loadFleetOperationalByAsset : loadFleetOperationalByGroup)();
    if (error) {
      showFailFeedback(error);
      setOperationals(undefined);
    } else if (data) {
      setOperationals(Array.isArray(data) ? data : [data]);
    } else {
      setOperationals(undefined);
    }
  };

  if (searchType === 0 && !selectedAssetId) {
    return (
      <FlexColumn classNames={`${classes.flexFill} ${css({ overflow: 'hidden' })}`}>
        <EmptyState title="Selecciona un activo para ver sus operaciones." />
      </FlexColumn>
    );
  }

  if (searchType === 1 && !selectedGroupId) {
    return (
      <FlexColumn classNames={`${classes.flexFill} ${css({ overflow: 'hidden' })}`}>
        <EmptyState title="Selecciona un grupo para listar sus operaciones." />
      </FlexColumn>
    );
  }

  if (!startDate || !endDate) {
    return (
      <FlexColumn classNames={`${classes.flexFill} ${css({ overflow: 'hidden' })}`}>
        <EmptyState title="Selecciona un rango de fechas para ver las operaciones." />
      </FlexColumn>
    );
  }

  const getCellStyle = (index: number): CSSProperties => {
    return {
      flex: tableColumnsName[index].flex,
      minWidth: tableColumnsName[index].minWidth,
      maxWidth: tableColumnsName[index].maxWidth,
      boxSizing: 'border-box',
      paddingLeft: theme.sizing.scale200,
      paddingRight: theme.sizing.scale200,
      justifyContent: tableColumnsName[index].centered ? 'center' : undefined,
    };
  };

  return (
    <FlexColumn classNames={`${classes.flexFill} ${css({ overflow: 'visible' })}`}>
      {!operationals && loading && <Loading />}
      {operationals && (
        <FlexGrid
          flexGridColumnCount={[1, 1, 1, 3, 4, 3]}
          flexGridColumnGap="scale600"
          flexGridRowGap="scale600"
          overflow="visible"
        >
          {resume.map((item, index) => {
            return (
              <FlexGridItem
                display="flex"
                key={index}
              >
                <StyledCard rootStyleOverrides={{ width: '100%', borderWidth: '2px' }}>
                  <StyledCardBodyWrapper>
                    <FlexColumn centered>
                      <FlexColumn
                        gap={0}
                        centered
                      >
                        <span style={{ ...theme.typography.HeadingXXLarge }}>{item.count}</span>
                        <LabelLarge style={{ textAlign: 'center' }}>{item.label}</LabelLarge>
                      </FlexColumn>
                      <FlexRow
                        centered
                        classNames={css({
                          borderRadius: theme.borders.radius200,
                          background: item.color,
                          width: '100%',
                          padding: theme.sizing.scale200,
                          boxSizing: 'border-box',
                        })}
                      >
                        <LabelLarge
                          color={theme.colors.contentInversePrimary}
                          style={{ fontWeight: '700' }}
                        >
                          {getFormattedNumber(item.total, { prefix: '$', trimmed: true })} Total
                        </LabelLarge>
                      </FlexRow>
                    </FlexColumn>
                  </StyledCardBodyWrapper>
                </StyledCard>
              </FlexGridItem>
            );
          })}
        </FlexGrid>
      )}
      {operationals && (
        <StyledTable>
          <StyledHead style={{ minWidth: `${TABLE_MIN_WIDTH}px` }}>
            {tableColumnsName.map((column, index) => (
              <StyledHeadCell
                key={index}
                style={{
                  flex: column.flex,
                  minWidth: column.minWidth ? `${column.minWidth}px` : undefined,
                  maxWidth: column.maxWidth ? `${column.maxWidth}px` : undefined,
                  justifyContent: column.centered ? 'center' : undefined,
                  paddingTop: theme.sizing.scale500,
                  paddingBottom: theme.sizing.scale500,
                  paddingLeft: theme.sizing.scale200,
                  paddingRight: theme.sizing.scale200,
                  boxSizing: 'border-box',
                  ...theme.typography.ParagraphSmall,
                }}
              >
                {column.label}
              </StyledHeadCell>
            ))}
          </StyledHead>
          {operationals.map((operational, index) => {
            return (
              <StyledRow
                key={index}
                className={css({
                  backgroundColor: (index + 1) % 2 === 0 ? theme.colors.tableStripedBackground : '',
                  minWidth: `${TABLE_MIN_WIDTH}px`,
                })}
              >
                <StyledCell style={getCellStyle(0)}>{operational.ident}</StyledCell>
                <StyledCell style={getCellStyle(1)}>{operational.label}</StyledCell>
                <StyledCell style={getCellStyle(2)}>{operational.maintenances}</StyledCell>
                <StyledCell style={getCellStyle(3)}>
                  {getFormattedNumber(operational.maintenanceCosts, {
                    prefix: '$',
                    trimmed: true,
                  })}
                </StyledCell>
                <StyledCell style={getCellStyle(4)}>{operational.reparations}</StyledCell>
                <StyledCell style={getCellStyle(5)}>
                  {getFormattedNumber(operational.reparation_costs, {
                    prefix: '$',
                    trimmed: true,
                  })}
                </StyledCell>
                <StyledCell style={getCellStyle(6)}>{operational.refuels}</StyledCell>
                <StyledCell style={getCellStyle(7)}>
                  {getFormattedNumber(operational.refuel, {
                    prefix: '$',
                    trimmed: true,
                  })}
                </StyledCell>
              </StyledRow>
            );
          })}
          <StyledDivider style={{ margin: 0, width: '100%' }} />
        </StyledTable>
      )}
    </FlexColumn>
  );
}

export default FleetOperationalList;
